import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, Percent, Filter, ListChecks, Search as SearchIcon, RotateCcw, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { inv } from '../../lib/invApi';

const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${parseFloat(v).toFixed(2)}`);
const pct = (v) => (v === null || v === undefined || v === '' ? '—' : `${v}%`);

// Bulk Pricing: Select → Configure → Preview → Confirm. Two targets: Markup or Cost.
const BulkPricingDialog = ({ open, onOpenChange, selectedIds, currentSearch, departments, categories, brands, suppliers, onDone }) => {
  const [step, setStep] = useState('config'); // config | preview | done
  const [selMode, setSelMode] = useState('filter'); // filter | selected | search
  const [dept, setDept] = useState(''); const [cat, setCat] = useState(''); const [brand, setBrand] = useState(''); const [supplier, setSupplier] = useState('');
  const [target, setTarget] = useState('markup');
  const [markupMode, setMarkupMode] = useState('set'); const [markupValue, setMarkupValue] = useState('');
  const [costMode, setCostMode] = useState('increase_pct'); const [costValue, setCostValue] = useState('');
  const [recalcSell, setRecalcSell] = useState(true);
  const [rounding, setRounding] = useState('none');
  const [overwrite, setOverwrite] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  useEffect(() => {
    if (open) {
      setStep('config'); setPreview(null); setLastEvent(null);
      setSelMode(selectedIds?.length ? 'selected' : 'filter');
      setDept(''); setCat(''); setBrand(''); setSupplier('');
      setTarget('markup'); setMarkupMode('set'); setMarkupValue(''); setCostMode('increase_pct'); setCostValue('');
      setRecalcSell(true); setRounding('none'); setOverwrite(false); setReason('');
    }
  }, [open]); // eslint-disable-line

  const buildConfig = () => {
    const c = { target, rounding, overwrite_overrides: overwrite, reason };
    if (selMode === 'selected') c.item_ids = selectedIds;
    else if (selMode === 'search') c.search = currentSearch || '';
    else { if (dept) c.department = dept; if (cat) c.category = cat; if (brand) c.brand = brand; if (supplier) c.supplier = supplier; }
    if (target === 'markup') { c.markup_mode = markupMode; c.markup_value = parseFloat(markupValue) || 0; }
    else { c.cost_mode = costMode; c.cost_value = parseFloat(costValue) || 0; c.recalc_sell = recalcSell; }
    return c;
  };

  const validConfig = target === 'markup' ? markupValue !== '' : costValue !== '';

  const doPreview = async () => {
    if (!validConfig) { toast.error('Enter an adjustment value'); return; }
    setBusy(true);
    try { const { data } = await inv.post('/items/bulk-price/preview', buildConfig()); setPreview(data); setStep('preview'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Preview failed'); }
    setBusy(false);
  };
  const doApply = async () => {
    setBusy(true);
    try { const { data } = await inv.post('/items/bulk-price/apply', buildConfig()); setLastEvent(data); setStep('done'); toast.success(data.message); onDone?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Apply failed'); }
    setBusy(false);
  };
  const doUndo = async () => {
    if (!lastEvent) return;
    try { const { data } = await inv.post(`/items/bulk-price/undo/${lastEvent.event_id}`); toast.success(data.message); setLastEvent(null); onDone?.(); onOpenChange(false); }
    catch (err) { toast.error(err.response?.data?.detail || 'Undo failed'); }
  };

  const s = preview?.summary;
  const isCost = target === 'cost';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inv-bulk-price-dialog" className="bg-[#141414] border-[#262626] text-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{isCost ? <DollarSign className="w-5 h-5 text-[#D4AF37]" /> : <Percent className="w-5 h-5 text-[#D4AF37]" />} Bulk Pricing</DialogTitle>
          <DialogDescription className="text-[#A3A3A3] text-xs">Select items, configure the change, preview, then confirm. Nothing changes until you confirm. Only pricing fields are touched — never stock, barcodes, targets, loadouts or history.</DialogDescription>
        </DialogHeader>

        {step === 'config' && (
          <div className="space-y-4">
            {/* Selection */}
            <div className="border border-[#262626] rounded-md p-3 space-y-3">
              <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold flex items-center gap-2"><Filter className="w-4 h-4 text-[#D4AF37]" /> Which items?</p>
              <div className="flex flex-wrap gap-2 text-sm">
                <SegBtn active={selMode === 'filter'} onClick={() => setSelMode('filter')} testId="inv-bulk-mode-filter"><Filter className="w-3.5 h-3.5 mr-1" /> By attributes</SegBtn>
                <SegBtn active={selMode === 'selected'} onClick={() => setSelMode('selected')} testId="inv-bulk-mode-selected" disabled={!selectedIds?.length}><ListChecks className="w-3.5 h-3.5 mr-1" /> Selected items ({selectedIds?.length || 0})</SegBtn>
                <SegBtn active={selMode === 'search'} onClick={() => setSelMode('search')} testId="inv-bulk-mode-search" disabled={!currentSearch}><SearchIcon className="w-3.5 h-3.5 mr-1" /> Current search "{currentSearch || '—'}"</SegBtn>
              </div>
              {selMode === 'filter' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <FilterSel label="Department" testId="inv-bulk-dept" value={dept} onChange={setDept} options={departments.filter((d) => d.active !== false).map((d) => d.name)} />
                  <FilterSel label="Category" testId="inv-bulk-cat" value={cat} onChange={setCat} options={categories.filter((c) => c.active !== false).map((c) => c.name)} />
                  <FilterSel label="Brand" testId="inv-bulk-brand" value={brand} onChange={setBrand} options={brands.filter((b) => b.active !== false).map((b) => b.name)} />
                  <FilterSel label="Preferred Supplier" testId="inv-bulk-supplier" value={supplier} onChange={setSupplier} options={suppliers.filter((x) => x.active !== false).map((x) => x.name)} />
                </div>
              )}
              {selMode === 'filter' && <p className="text-[10px] text-[#525252]">Leave all blank for ALL active inventory. Filters combine (e.g. Supplier = Ferguson AND Department = Plumbing).</p>}
            </div>

            {/* Adjustment */}
            <div className="border border-[#262626] rounded-md p-3 space-y-3">
              <p className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold">What are you changing?</p>
              <div className="flex gap-2">
                <SegBtn active={target === 'markup'} onClick={() => setTarget('markup')} testId="inv-bulk-target-markup"><Percent className="w-3.5 h-3.5 mr-1" /> Markup (customer price)</SegBtn>
                <SegBtn active={target === 'cost'} onClick={() => setTarget('cost')} testId="inv-bulk-target-cost"><DollarSign className="w-3.5 h-3.5 mr-1" /> Default Cost (what we pay)</SegBtn>
              </div>

              {target === 'markup' ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <SegBtn small active={markupMode === 'set'} onClick={() => setMarkupMode('set')} testId="inv-bulk-markup-set">Set markup to %</SegBtn>
                    <SegBtn small active={markupMode === 'increase'} onClick={() => setMarkupMode('increase')} testId="inv-bulk-markup-increase">Increase by percentage points</SegBtn>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input data-testid="inv-bulk-markup-value" type="number" step="any" value={markupValue} onChange={(e) => setMarkupValue(e.target.value)} placeholder={markupMode === 'set' ? 'e.g. 200' : 'e.g. 25'} className="w-32 bg-[#0A0A0A] border-[#262626] text-white" />
                    <span className="text-[#A3A3A3] text-xs">{markupMode === 'set' ? 'Sell = Cost × (1 + markup/100). 200% → Sell = Cost × 3.' : 'Adds points to each item\'s current markup. 200% + 25 pts = 225% (NOT ×1.25).'}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <SegBtn small active={costMode === 'increase_pct'} onClick={() => setCostMode('increase_pct')} testId="inv-bulk-cost-increase">Increase cost by %</SegBtn>
                    <SegBtn small active={costMode === 'decrease_pct'} onClick={() => setCostMode('decrease_pct')} testId="inv-bulk-cost-decrease">Decrease cost by %</SegBtn>
                    <SegBtn small active={costMode === 'set'} onClick={() => setCostMode('set')} testId="inv-bulk-cost-set">Set cost to $</SegBtn>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input data-testid="inv-bulk-cost-value" type="number" step="any" value={costValue} onChange={(e) => setCostValue(e.target.value)} placeholder={costMode === 'set' ? 'e.g. 10.50' : 'e.g. 5'} className="w-32 bg-[#0A0A0A] border-[#262626] text-white" />
                    <span className="text-[#A3A3A3] text-xs">{costMode === 'increase_pct' ? '$10.00 + 5% = $10.50' : costMode === 'decrease_pct' ? '$10.00 − 5% = $9.50' : 'Sets Default Cost directly for the selected items.'}</span>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input data-testid="inv-bulk-recalc-sell" type="checkbox" checked={recalcSell} onChange={(e) => setRecalcSell(e.target.checked)} className="accent-[#D4AF37]" />
                    Recalculate Sell Price from existing Markup % <span className="text-[#525252] text-xs">(markup % itself stays the same)</span>
                  </label>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Sell price rounding</Label>
                  <select data-testid="inv-bulk-rounding" value={rounding} onChange={(e) => setRounding(e.target.value)} className="w-full h-9 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
                    <option value="none">Exact (2 decimals)</option>
                    <option value="up_0.5">Round up to nearest $0.50</option>
                    <option value="up_1">Round up to nearest $1</option>
                    <option value="up_5">Round up to nearest $5</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Manual Sell overrides</Label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer h-9">
                    <input data-testid="inv-bulk-overwrite" type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-[#D4AF37]" />
                    {overwrite ? 'Overwrite manual overrides too' : 'Skip manual overrides (default)'}
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs">Reason / note (saved to history)</Label>
                <Input data-testid="inv-bulk-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Ferguson 5% supplier increase — August 2026" className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button onClick={() => onOpenChange(false)} variant="outline" className="border-[#262626]">Cancel</Button>
              <Button data-testid="inv-bulk-preview-btn" onClick={doPreview} disabled={busy || !validConfig} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{busy ? 'Loading…' : 'Preview Changes'} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Stat label="Selected" value={s.items_selected} />
              <Stat label="Will update" value={s.items_changing} accent />
              <Stat label="Missing cost" value={s.missing_cost} />
              <Stat label="Overrides skipped" value={s.overrides_skipped} />
              <Stat label="Avg sell" value={`${money(s.avg_old_sell)} → ${money(s.avg_new_sell)}`} />
            </div>
            <div className="border border-[#262626] rounded-md overflow-x-auto max-h-[48vh]">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] border-b border-[#262626] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Item</th>
                    {isCost && <th className="text-left px-2 py-2 text-[#A3A3A3]">Supplier</th>}
                    <th className="text-right px-2 py-2 text-[#A3A3A3]">Cost</th>
                    {isCost && <th className="text-right px-2 py-2 text-[#A3A3A3]">New Cost</th>}
                    <th className="text-right px-2 py-2 text-[#A3A3A3]">Old Markup</th>
                    {!isCost && <th className="text-right px-2 py-2 text-[#A3A3A3]">New Markup</th>}
                    <th className="text-right px-2 py-2 text-[#A3A3A3]">Old Sell</th>
                    <th className="text-right px-2 py-2 text-[#A3A3A3]">New Sell</th>
                    <th className="text-left px-2 py-2 text-[#A3A3A3]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.item_id} data-testid={`inv-bulk-row-${r.item_id}`} className="border-b border-[#262626] last:border-0">
                      <td className="px-2 py-1.5 text-white">{r.name}</td>
                      {isCost && <td className="px-2 py-1.5 text-[#A3A3A3]">{r.supplier}</td>}
                      <td className="px-2 py-1.5 text-right text-[#A3A3A3]">{money(r.old_cost)}{isCost && r.cost_change_label ? <span className="text-[#525252]"> {r.cost_change_label}</span> : ''}</td>
                      {isCost && <td className="px-2 py-1.5 text-right text-white">{money(r.new_cost)}</td>}
                      <td className="px-2 py-1.5 text-right text-[#A3A3A3]">{pct(r.old_markup)}</td>
                      {!isCost && <td className="px-2 py-1.5 text-right text-white">{pct(r.new_markup)}</td>}
                      <td className="px-2 py-1.5 text-right text-[#A3A3A3]">{money(r.old_sell)}</td>
                      <td className={`px-2 py-1.5 text-right font-medium ${r.status === 'will_update' ? 'text-[#D4AF37]' : 'text-[#525252]'}`}>{money(r.new_sell)}</td>
                      <td className="px-2 py-1.5">
                        {r.status === 'will_update' && <span className="text-green-400">Update{isCost && !r.sell_recalculated && r.old_override ? ' (sell kept)' : ''}</span>}
                        {r.status === 'skip_override' && <span className="text-amber-400">Manual override — skipped</span>}
                        {r.status === 'missing_cost' && <span className="text-red-400"><AlertTriangle className="w-3 h-3 inline mr-0.5" />Missing cost</span>}
                        {r.status === 'no_change' && <span className="text-[#525252]">No change</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-2">
              <Button data-testid="inv-bulk-back" onClick={() => setStep('config')} variant="outline" className="border-[#262626]">Back</Button>
              <Button data-testid="inv-bulk-apply-btn" onClick={doApply} disabled={busy || s.items_changing === 0} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{busy ? 'Applying…' : `Confirm — Update ${s.items_changing} item(s)`}</Button>
            </div>
          </div>
        )}

        {step === 'done' && lastEvent && (
          <div data-testid="inv-bulk-done" className="space-y-4 py-4 text-center">
            <p className="text-green-400 text-lg font-semibold">{lastEvent.message}</p>
            <p className="text-[#A3A3A3] text-sm">Saved to pricing history{reason ? ` with reason "${reason}"` : ''}. You can undo this change if it wasn't what you intended.</p>
            <div className="flex justify-center gap-2">
              <Button data-testid="inv-bulk-undo-btn" onClick={doUndo} variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20"><RotateCcw className="w-4 h-4 mr-1" /> Undo This Change</Button>
              <Button data-testid="inv-bulk-close-btn" onClick={() => onOpenChange(false)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SegBtn = ({ active, onClick, children, testId, disabled, small }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled} className={`inline-flex items-center rounded-md border transition-colors ${small ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} ${active ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'bg-[#0A0A0A] text-white border-[#262626] hover:border-[#D4AF37]/40'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>{children}</button>
);
const FilterSel = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-[10px] text-[#A3A3A3]">{label}</Label>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 rounded bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
      <option value="">Any</option>
      {[...new Set(options)].map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const Stat = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md px-2 py-1.5 text-center">
    <p className={`text-sm font-bold ${accent ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</p>
    <p className="text-[9px] uppercase tracking-wider text-[#525252]">{label}</p>
  </div>
);

export default BulkPricingDialog;
